import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiX, FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiUser, FiCalendar, FiSave, FiSend } from 'react-icons/fi';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

/* Выше типичных модалок (SidebarNav ~2e5, Company* ~1e6) и главного слоя онбординга на телефоне */
const Z_ONBOARD_MAIN_MOBILE = 10010000;
const Z_ONBOARD_NESTED_MOBILE = 10010100;

const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$isMobile',
})`
  position: fixed !important;
  top: 0 !important;
  left: ${(p) => (p.$isMobile ? '0' : '320px')} !important;
  right: 0 !important;
  bottom: 0 !important;
  width: ${(p) => (p.$isMobile ? '100vw' : 'calc(100vw - 320px)')} !important;
  height: 100vh !important;
  margin: 0 !important;
  padding: 0 !important;
  background: rgba(0, 0, 0, 0.45) !important;
  display: flex !important;
  align-items: stretch !important;
  justify-content: flex-start !important;
  z-index: ${(p) => (p.$isMobile ? String(Z_ONBOARD_MAIN_MOBILE) : '999999')} !important;
  box-sizing: border-box !important;
  /* pointer-events:none на оверлее ломал hit-test в iOS/WebKit: тачи не доходили до кнопок в scroll-area */
  pointer-events: auto !important;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  /* body в приложении задаёт color: #f8f8f8 — без своего цвета белая модалка наследует «белый» текст */
  color: #0f172a;
  border-radius: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  min-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  pointer-events: auto !important;
  touch-action: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  flex-shrink: 0;
  touch-action: manipulation;
  color: #ffffff;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 1.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 12px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 24px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.4);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

/** Якорь для frontendSettings.js: не красить заголовки/лейблы «под тёмную модалку» */
const ONBOARDING_LIGHT_UI_CLASS = 'onboarding-modal-light-ui';
/** Имя компонента даёт class с подстрокой "header" → попадает в [class*="header"] глобальных стилей */
const ONBOARDING_CATEGORY_ROW_CLASS = 'onboarding-category-row';

const ModalBody = styled.div.attrs((p) => ({
  className: [ONBOARDING_LIGHT_UI_CLASS, p.className].filter(Boolean).join(' '),
}))`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f9fafb;
  min-height: 0;
  touch-action: auto;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  flex-wrap: wrap;
  flex-shrink: 0;
  position: relative;
  touch-action: manipulation;
`;

const AddTaskButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(102, 126, 234, 0.35);
  position: relative;
  z-index: 1;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }

  & svg {
    pointer-events: none;
  }
`;

const TasksContainer = styled.div.attrs((p) => ({
  className: [ONBOARDING_LIGHT_UI_CLASS, p.className].filter(Boolean).join(' '),
}))`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  background: #f9fafb;
  position: relative;
  z-index: 0;
  touch-action: auto;
  -webkit-overflow-scrolling: touch;
  pointer-events: auto !important;
`;

const CategoryHeader = styled.div.attrs((p) => ({
  className: [ONBOARDING_CATEGORY_ROW_CLASS, p.className].filter(Boolean).join(' '),
}))`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #ffffff;
  color: #0f172a !important;
  -webkit-text-fill-color: #0f172a !important;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => props.$isExpanded ? '#667eea' : '#e5e7eb'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(102, 126, 234, 0.2);
  position: relative;
  z-index: 0;

  & svg {
    color: #667eea !important;
    -webkit-text-fill-color: #667eea !important;
  }

  & h3 {
    color: #0f172a !important;
    -webkit-text-fill-color: #0f172a !important;
  }

  &:hover {
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  }
`;

const CategoryTitle = styled.h3`
  && {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a !important;
    -webkit-text-fill-color: #0f172a !important;
    flex: 1;
  }
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: #ffffff;
  color: #0f172a;
  border-radius: 10px;
  margin-bottom: 8px;
  margin-left: ${props => props.$level * 32}px;
  border: 1px solid #e5e7eb;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  touch-action: manipulation;
  position: relative;
  z-index: 1;
  pointer-events: auto !important;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
  }
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateX(4px);
    }
  }
`;

const TaskInfo = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: calc(100% - 192px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  order: 1;
  position: relative;
  z-index: 0;
  pointer-events: none;
`;

const TaskName = styled.div`
  && {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
  }
`;

const TaskMeta = styled.div`
  && {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 20px;
    font-size: 0.85rem;
    color: #4b5563;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TaskActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-grow: 0;
  margin-left: auto;
  order: 2;
  position: relative;
  z-index: 20;
  pointer-events: auto !important;
  touch-action: manipulation;
  transform: translateZ(0);
  isolation: isolate;
  min-width: 176px;
  justify-content: flex-end;
`;

const ActionButton = styled.button.attrs({ type: 'button' })`
  width: 40px;
  min-width: 40px;
  height: 40px;
  min-height: 40px;
  border: none;
  border-radius: 8px;
  background: ${props => {
    if (props.$type === 'add') return 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)';
    if (props.$type === 'edit') return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    if (props.$type === 'delete') return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    return '#f3f4f6';
  }};
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.35);
  position: relative;
  z-index: 3;
  pointer-events: auto !important;
  padding: 0;
  
  &:active {
    transform: scale(0.96);
  }
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: scale(1.08);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  & svg {
    pointer-events: none;
  }
`;

/* Главный онбординг на мобилке: z-index 1000080 — вложенные модалки ОБЯЗАНЫ быть выше, иначе окажутся под полупрозрачным родителем. */
const EditModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$isMobile',
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: ${(p) => (p.$isMobile ? 'stretch' : 'center')};
  justify-content: ${(p) => (p.$isMobile ? 'stretch' : 'center')};
  z-index: ${(p) => (p.$isMobile ? String(Z_ONBOARD_NESTED_MOBILE) : '1000002')} !important;
  padding: ${(p) => (p.$isMobile ? '0' : '20px')};
  box-sizing: border-box;
  pointer-events: auto !important;
`;

const EditModalContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$isMobile',
})`
  background: #ffffff;
  /* Иначе после снятия глобальных инлайнов лейблы/span наследуют body #f8f8f8 */
  color: #0f172a;
  border-radius: ${(p) => (p.$isMobile ? '0' : '16px')};
  width: ${(p) => (p.$isMobile ? '100%' : '90%')};
  max-width: ${(p) => (p.$isMobile ? 'none' : '800px')};
  max-height: ${(p) => (p.$isMobile ? '100vh' : '90vh')};
  min-height: ${(p) => (p.$isMobile ? '100vh' : 'auto')};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
`;

const EditModalTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
  font-size: 1.3rem;
  font-weight: 700;
`;

const EditModalBody = styled.div.attrs((p) => ({
  className: [ONBOARDING_LIGHT_UI_CLASS, p.className].filter(Boolean).join(' '),
}))`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  color: #0f172a;
  -webkit-text-fill-color: #0f172a;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151 !important;
  -webkit-text-fill-color: #374151 !important;
  
  &::after {
    content: ${props => props.$required ? '" *"' : '""'};
    color: #ef4444;
    -webkit-text-fill-color: #ef4444;
  }
`;

const RadioChoiceLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #0f172a !important;
  -webkit-text-fill-color: #0f172a !important;
  font-size: 0.95rem;

  span {
    color: inherit;
    -webkit-text-fill-color: inherit;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  -webkit-text-fill-color: #0f172a;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #ffffff;
  color: #0f172a;
  -webkit-text-fill-color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DateInput = styled(Input)`
  flex: 1;
  min-width: 100px;
`;

const DateText = styled.span`
  font-size: 0.9rem;
  color: #6b7280;
  white-space: nowrap;
`;

const CalendarButton = styled.button`
  padding: 10px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #667eea;
    background: #f9fafb;
  }
`;

const CalendarWrapper = styled.div`
  position: relative;
`;

const CalendarDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 10000;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  padding: 12px;
  
  .react-calendar {
    border: none;
    font-family: inherit;
    width: 100%;
    max-width: 350px;
    
    &__tile {
      border-radius: 6px;
      padding: 10px 6px;
      transition: all 0.2s ease;
      font-size: 0.9rem;
      
      &:hover {
        background: #f3f4f6;
      }
      
      &--active {
        background: #667eea !important;
        color: #ffffff !important;
      }
      
      &--now {
        background: #fef3c7;
        color: #92400e;
      }
      
      &--disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
    
    &__navigation {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      
      button {
        border-radius: 6px;
        min-width: 36px;
        height: 36px;
        transition: all 0.2s ease;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        color: #374151;
        
        &:hover {
          background: #f3f4f6;
        }
        
        &:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
      }
    }
    
    &__month-view__weekdays {
      text-align: center;
      font-weight: 600;
      font-size: 0.85rem;
      color: #6b7280;
      margin-bottom: 8px;
    }
    
    &__month-view__days__day {
      font-size: 0.9rem;
    }
  }
`;

const RichTextEditor = styled.div`
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  min-height: 250px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditorToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: ${props => props.$active ? '#667eea' : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#374151'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: ${props => props.$bold ? 'bold' : 'normal'};
  border: 1px solid ${props => props.$active ? '#667eea' : '#e5e7eb'};
  
  &:hover {
    background: ${props => props.$active ? '#5568d3' : '#f3f4f6'};
    border-color: ${props => props.$active ? '#5568d3' : '#d1d5db'};
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const EditorContent = styled.div`
  flex: 1;
  padding: 16px;
  border: none;
  outline: none;
  color: #111827;
  font-size: 0.95rem;
  line-height: 1.6;
  font-family: inherit;
  min-height: 200px;
  overflow-y: auto;
  word-wrap: break-word;
  
  &:focus {
    outline: none;
  }
  
  &:empty::before {
    content: 'Опишите задание...';
    color: #9ca3af;
    pointer-events: none;
  }
  
  /* Стили для форматированного текста */
  strong {
    font-weight: bold;
  }
  
  em {
    font-style: italic;
  }
  
  u {
    text-decoration: underline;
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 4px 0;
  }
  
  a {
    color: #667eea;
    text-decoration: underline;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 8px 0;
  }
  
  p {
    margin: 8px 0;
    
    &:first-child {
      margin-top: 0;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

export default function OnboardingModal({ open, onClose, isMobile = false }) {
  const [tasks, setTasks] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    name: '',
    assignedTo: 'person_concerned',
    assignedPerson: '',
    startDateType: 'days_before_hire', // 'days_before_hire', 'on_hire_date', 'after_parent', 'specific_date'
    startDateValue: '',
    startSpecificDate: null,
    endDateType: 'days_after_start', // 'days_after_start', 'same_day', 'specific_date'
    endDateValue: '',
    endSpecificDate: null,
    description: ''
  });
  const editorRef = useRef(null);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);
  const editorInitializedRef = useRef(false);
  const [showSendTaskModal, setShowSendTaskModal] = useState(false);
  const [selectedTaskForSend, setSelectedTaskForSend] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [sendDate, setSendDate] = useState('');
  const [sendTime, setSendTime] = useState('');
  const [sendImmediately, setSendImmediately] = useState(true);

  /* Синтетический click после тача часто приходит на фон вложенной модалки и закрывает её.
     Guard в useEffect включался ПОСЛЕ paint — click успевал раньше. Включаем синхронно в handle* перед setState. */
  const nestModalBackdropGuardRef = useRef(false);
  const nestGuardClearTimerRef = useRef(null);

  const armNestModalBackdropGuard = useCallback(() => {
    nestModalBackdropGuardRef.current = true;
    if (nestGuardClearTimerRef.current) {
      window.clearTimeout(nestGuardClearTimerRef.current);
    }
    nestGuardClearTimerRef.current = window.setTimeout(() => {
      nestModalBackdropGuardRef.current = false;
      nestGuardClearTimerRef.current = null;
    }, 950);
  }, []);

  const tryCloseEditNestedModal = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    if (nestModalBackdropGuardRef.current) return;
    setEditingTask(null);
  }, []);

  const tryCloseSendTaskModal = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    if (nestModalBackdropGuardRef.current) return;
    setShowSendTaskModal(false);
  }, []);

  const suppressSyntheticClickUntilRef = useRef(0);
  const lastPointerActionAtRef = useRef(0);

  const mobileTap = useCallback((fn) => {
    if (!isMobile) {
      return {
        onClick: (e) => {
          e.stopPropagation();
          fn(e);
        },
      };
    }
    return {
      onPointerUp: (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.stopPropagation();
        const now = Date.now();
        if (now - lastPointerActionAtRef.current < 280) return;
        lastPointerActionAtRef.current = now;
        suppressSyntheticClickUntilRef.current = now + 700;
        fn(e);
      },
      onClick: (e) => {
        e.stopPropagation();
        if (Date.now() < suppressSyntheticClickUntilRef.current) {
          e.preventDefault();
          return;
        }
        const now = Date.now();
        if (now - lastPointerActionAtRef.current < 280) return;
        lastPointerActionAtRef.current = now;
        fn(e);
      },
    };
  }, [isMobile]);

  // Сброс флага инициализации при закрытии модалки редактирования
  useEffect(() => {
    if (editingTask === null) {
      editorInitializedRef.current = false;
    }
  }, [editingTask]);

  // Обработчик ESC для закрытия модалки
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        if (editingTask !== null) {
          setEditingTask(null);
        } else {
          onClose();
        }
      }
    };
    
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose, editingTask]);

  // Загрузка списка пользователей
  useEffect(() => {
    if (open) {
      const token = localStorage.getItem('token');
      if (token) {
        fetch('/api/users', {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              setUsers(data);
            } else if (data.users && Array.isArray(data.users)) {
              setUsers(data.users);
            }
          })
          .catch(err => {
            console.error('Ошибка загрузки пользователей:', err);
            setUsers([]);
          });
      }
    }
  }, [open]);

  // Загрузка задач онбординга (пока заглушка)
  useEffect(() => {
    if (open) {
      // TODO: Загрузить задачи с сервера
      setTasks([
        {
          id: 1,
          category: 'Организация рабочего места',
          name: 'Организация рабочего места',
          assignedTo: 'specific',
          assignedPerson: 'Олена Попович',
          startDateType: 'days_before_hire',
          startDateValue: '1',
          endDateType: 'days_after_start',
          endDateValue: '3',
          description: '',
          children: []
        },
        {
          id: 2,
          category: 'Организация рабочего места',
          name: 'Рабочее место',
          assignedTo: 'person_concerned',
          assignedPerson: '',
          startDateType: 'after_parent',
          startDateValue: '',
          endDateType: 'days_after_start',
          endDateValue: '2',
          description: 'Твой куратор уже показал тебе твое рабочее место 🧑‍💻\nОцени по 5-балльной шкале насколько оно тебе нравится?) Возможно, тебе что-то нужно для\nболее удобной и продуктивной работы?\nНапиши об этом в комментариях 👇',
          parentId: 1,
          children: []
        }
      ]);
    }
  }, [open]);

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Закрытие календарей при клике вне их
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startCalendarRef.current && !startCalendarRef.current.contains(event.target)) {
        setShowStartCalendar(false);
      }
      if (endCalendarRef.current && !endCalendarRef.current.contains(event.target)) {
        setShowEndCalendar(false);
      }
    };
    
    if (showStartCalendar || showEndCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStartCalendar, showEndCalendar]);

  const handleAddTask = () => {
    armNestModalBackdropGuard();
    setTaskForm({
      name: '',
      assignedTo: 'person_concerned',
      assignedPerson: '',
      startDateType: 'days_before_hire',
      startDateValue: '',
      startSpecificDate: null,
      endDateType: 'days_after_start',
      endDateValue: '',
      endSpecificDate: null,
      description: ''
    });
    setEditingTask({ id: null, isNew: true });
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    
    // Очищаем редактор после монтирования
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '<p><br></p>';
      }
    }, 0);
  };

  const handleAddSubtask = (parentTask) => {
    armNestModalBackdropGuard();
    setTaskForm({
      name: '',
      assignedTo: 'person_concerned',
      assignedPerson: '',
      startDateType: 'after_parent',
      startDateValue: '',
      startSpecificDate: null,
      endDateType: 'days_after_start',
      endDateValue: '',
      endSpecificDate: null,
      description: '',
      parentId: parentTask.id,
      category: parentTask.category || 'Новая категория'
    });
    setEditingTask({ id: null, isNew: true, isSubtask: true, parentId: parentTask.id });
    setShowStartCalendar(false);
    setShowEndCalendar(false);
    
    // Очищаем редактор после монтирования
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '<p><br></p>';
      }
    }, 0);
  };

  const handleEditTask = (task) => {
    armNestModalBackdropGuard();
    setTaskForm({
      name: task.name,
      assignedTo: task.assignedTo || 'person_concerned',
      assignedPerson: task.assignedPerson || '',
      startDateType: task.startDateType || 'days_before_hire',
      startDateValue: task.startDateValue || '',
      startSpecificDate: task.startSpecificDate ? new Date(task.startSpecificDate) : null,
      endDateType: task.endDateType || 'days_after_start',
      endDateValue: task.endDateValue || '',
      endSpecificDate: task.endSpecificDate ? new Date(task.endSpecificDate) : null,
      description: task.description || '',
      parentId: task.parentId || null,
      category: task.category || 'Новая категория'
    });
    setEditingTask({ id: task.id, isNew: false });
    setShowStartCalendar(false);
    setShowEndCalendar(false);
  };

  const handleDeleteTask = (taskId) => {
    if (window.confirm('Вы уверены, что хотите удалить это задание?')) {
      setTasks(prevTasks => {
        // Удаляем задачу и все её подзадания
        return prevTasks
          .filter(t => t.id !== taskId)
          .map(t => {
            // Удаляем подзадание из children, если оно есть
            if (t.children && t.children.length > 0) {
              return {
                ...t,
                children: t.children.filter(child => child.id !== taskId)
              };
            }
            return t;
          });
      });
    }
  };

  const handleSendTask = (task) => {
    armNestModalBackdropGuard();
    setSelectedTaskForSend(task);
    setShowSendTaskModal(true);
    setSelectedUserId('');
    setSelectedUserName('');
    setSendDate('');
    setSendTime('');
    setSendImmediately(true);
  };

  const handleConfirmSendTask = async () => {
    if (!selectedTaskForSend) return;
    
    if (!selectedUserId) {
      alert('Пожалуйста, выберите пользователя');
      return;
    }

    if (!sendImmediately && (!sendDate || !sendTime)) {
      alert('Пожалуйста, укажите дату и время отправки');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const taskData = {
        task_id: selectedTaskForSend.id,
        task_name: selectedTaskForSend.name,
        task_description: selectedTaskForSend.description || '',
        task_category: selectedTaskForSend.category || '',
        assigned_to_user_id: parseInt(selectedUserId),
        assigned_to_username: selectedUserName
      };

      if (sendImmediately) {
        // Отправляем немедленно
        const response = await fetch('/api/onboarding/send-task', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(taskData)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Ошибка ответа сервера:', response.status, errorText);
          throw new Error(`Ошибка сервера: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          console.log('✅ [ONBOARDING] Task sent successfully:', result.task);
          alert('Задача успешно отправлена пользователю!');
          setShowSendTaskModal(false);
          setSelectedTaskForSend(null);
          
          // Обновляем список задач, если есть глобальное событие для обновления
          if (window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('taskCreated'));
          }
        } else {
          alert('Ошибка отправки задачи: ' + (result.error || 'Неизвестная ошибка'));
        }
      } else {
        // Планируем отправку
        // Формируем datetime в локальном времени пользователя
        // Важно: создаем Date объект, который интерпретирует время как локальное
        const [year, month, day] = sendDate.split('-').map(Number);
        const [hours, minutes] = sendTime.split(':').map(Number);
        
        // Создаем Date объект в локальном времени
        const localDate = new Date(year, month - 1, day, hours, minutes, 0);
        
        // Конвертируем в UTC для сохранения в БД (сервер работает в UTC)
        const utcDateTime = localDate.toISOString().slice(0, 19).replace('T', ' ');
        
        console.log('📅 [ONBOARDING] Scheduling task:', {
          sendDate,
          sendTime,
          localDateTime: localDate.toLocaleString('ru-RU'),
          utcDateTime,
          localISO: localDate.toISOString()
        });
        
        const response = await fetch('/api/onboarding/schedule-task', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            ...taskData,
            scheduled_date: sendDate,
            scheduled_time: sendTime,
            scheduled_datetime: utcDateTime // Отправляем в UTC для корректного сравнения
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Ошибка ответа сервера:', response.status, errorText);
          throw new Error(`Ошибка сервера: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        if (result.success) {
          console.log('✅ [ONBOARDING] Task scheduled successfully:', result.task);
          alert(`Задача запланирована на отправку ${sendDate} в ${sendTime}`);
          setShowSendTaskModal(false);
          setSelectedTaskForSend(null);
        } else {
          alert('Ошибка планирования задачи: ' + (result.error || 'Неизвестная ошибка'));
        }
      }
    } catch (error) {
      console.error('Ошибка отправки задачи:', error);
      alert('Ошибка отправки задачи: ' + error.message);
    }
  };

  const handleSaveTask = () => {
    // TODO: Сохранить задачу на сервере
    const taskData = {
      ...taskForm,
      startSpecificDate: taskForm.startSpecificDate ? taskForm.startSpecificDate.toISOString() : null,
      endSpecificDate: taskForm.endSpecificDate ? taskForm.endSpecificDate.toISOString() : null
    };
    
    if (editingTask.isNew) {
      const parentId = editingTask.parentId || taskForm.parentId;
      const newTask = {
        id: Date.now(),
        category: taskForm.category || 'Новая категория',
        ...taskData,
        parentId: parentId || null,
        children: []
      };
      
      console.log('💾 [ONBOARDING] Saving new task:', {
        isSubtask: !!parentId,
        parentId: parentId,
        taskName: newTask.name,
        category: newTask.category
      });
      
      if (parentId) {
        // Добавляем как подзадание к существующему заданию
        console.log('💾 [ONBOARDING] Adding as subtask to parent:', parentId);
        setTasks(prevTasks => {
          const updatedTasks = prevTasks.map(t => {
            if (t.id === parentId) {
              const updatedChildren = [...(t.children || []), newTask];
              console.log('💾 [ONBOARDING] Parent task updated, children count:', updatedChildren.length);
              return {
                ...t,
                children: updatedChildren
              };
            }
            return t;
          });
          console.log('💾 [ONBOARDING] Updated tasks:', updatedTasks.map(t => ({
            id: t.id,
            name: t.name,
            childrenCount: t.children?.length || 0
          })));
          return updatedTasks;
        });
      } else {
        // Добавляем как новое основное задание
        console.log('💾 [ONBOARDING] Adding as main task');
        setTasks(prevTasks => {
          const newTasks = [...prevTasks, newTask];
          console.log('💾 [ONBOARDING] Total tasks after add:', newTasks.length);
          return newTasks;
        });
      }
    } else {
      setTasks(prevTasks => prevTasks.map(t => {
        if (t.id === editingTask.id) {
          return { ...t, ...taskData };
        }
        // Также обновляем в подзаданиях
        if (t.children && t.children.length > 0) {
          return {
            ...t,
            children: t.children.map(child => 
              child.id === editingTask.id ? { ...child, ...taskData } : child
            )
          };
        }
        return t;
      }));
    }
    setEditingTask(null);
    setShowStartCalendar(false);
    setShowEndCalendar(false);
  };

  const getStartDateText = (task) => {
    if (task.startDateType === 'days_before_hire') {
      return `${task.startDateValue || 0} дней до найма`;
    } else if (task.startDateType === 'after_parent') {
      return 'После завершения родительского задания';
    } else if (task.startDateType === 'on_hire_date') {
      return 'На дату найма';
    } else if (task.startDateType === 'specific_date' && task.startSpecificDate) {
      const date = new Date(task.startSpecificDate);
      return date.toLocaleDateString('ru-RU');
    }
    return '';
  };

  const getEndDateText = (task) => {
    if (task.endDateType === 'days_after_start') {
      return `Через ${task.endDateValue || 0} дней`;
    } else if (task.endDateType === 'same_day') {
      return 'Того же дня';
    } else if (task.endDateType === 'specific_date' && task.endSpecificDate) {
      const date = new Date(task.endSpecificDate);
      return date.toLocaleDateString('ru-RU');
    }
    return '';
  };

  const getAssignedText = (task) => {
    if (task.assignedTo === 'specific') {
      return task.assignedPerson || 'Не указано';
    }
    return 'Человек которого это касается';
  };

  // Группировка задач по категориям с учетом подзаданий
  const tasksByCategory = tasks.reduce((acc, task) => {
    if (!acc[task.category]) {
      acc[task.category] = [];
    }
    // Добавляем только основные задачи (без parentId), подзадания отображаются внутри них
    if (!task.parentId) {
      acc[task.category].push(task);
    }
    return acc;
  }, {});
  
  // Функция для получения всех задач (включая подзадания) для отображения
  const getAllTasksForDisplay = (categoryTasks) => {
    const result = [];
    categoryTasks.forEach(task => {
      result.push(task);
      // Добавляем подзадания после основного задания
      if (task.children && task.children.length > 0) {
        result.push(...task.children);
      }
    });
    return result;
  };

  if (!open) return null;

  return (
    <>
      <ModalOverlay
        $isMobile={isMobile}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              📋 Онбординг
            </ModalTitle>
            <CloseBtn onClick={onClose}>
              <FiX />
            </CloseBtn>
          </ModalHeader>
          <ModalBody>
            <Toolbar>
              <AddTaskButton {...mobileTap(() => handleAddTask())}>
                <FiPlus />
                Новое задание
              </AddTaskButton>
            </Toolbar>
            <TasksContainer>
              {Object.entries(tasksByCategory).map(([category, categoryTasks]) => (
                <div key={category}>
                  <CategoryHeader
                    $isExpanded={expandedCategories.has(category)}
                    {...mobileTap(() => toggleCategory(category))}
                  >
                    {expandedCategories.has(category) ? (
                      <FiChevronDown size={20} color="#667eea" />
                    ) : (
                      <FiChevronRight size={20} color="#667eea" />
                    )}
                    <CategoryTitle>{category}</CategoryTitle>
                  </CategoryHeader>
                  {expandedCategories.has(category) && getAllTasksForDisplay(categoryTasks).map(task => (
                    <TaskItem key={task.id} $level={task.parentId ? 1 : 0}>
                      <TaskInfo>
                        <TaskName>{task.name}</TaskName>
                        <TaskMeta>
                          <MetaItem>
                            <FiUser size={16} />
                            {getAssignedText(task)}
                          </MetaItem>
                          <MetaItem>
                            <FiCalendar size={16} />
                            {getStartDateText(task)} - {getEndDateText(task)}
                          </MetaItem>
                        </TaskMeta>
                      </TaskInfo>
                      <TaskActions>
                        {!task.parentId && (
                          <>
                            <ActionButton
                              $type="add"
                              title="Отправить задачу пользователю"
                              {...mobileTap((e) => {
                                e?.stopPropagation?.();
                                handleSendTask(task);
                              })}
                            >
                              <FiSend size={16} />
                            </ActionButton>
                            <ActionButton
                              $type="add"
                              title="Добавить подзадание"
                              {...mobileTap((e) => {
                                e?.stopPropagation?.();
                                handleAddSubtask(task);
                              })}
                            >
                              <FiPlus size={16} />
                            </ActionButton>
                          </>
                        )}
                        <ActionButton
                          $type="edit"
                          title="Редактировать"
                          {...mobileTap(() => handleEditTask(task))}
                        >
                          <FiEdit2 size={16} />
                        </ActionButton>
                        <ActionButton
                          $type="delete"
                          title="Удалить"
                          {...mobileTap(() => handleDeleteTask(task.id))}
                        >
                          <FiTrash2 size={16} />
                        </ActionButton>
                      </TaskActions>
                    </TaskItem>
                  ))}
                </div>
              ))}
              {tasks.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                  <p style={{ fontSize: '1.1rem', marginBottom: '12px' }}>Нет задач онбординга</p>
                  <p style={{ fontSize: '0.9rem' }}>Нажмите "Новое задание" чтобы добавить первое задание</p>
                </div>
              )}
            </TasksContainer>
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>

      {editingTask !== null &&
        createPortal(
        <EditModalOverlay $isMobile={isMobile} onClick={tryCloseEditNestedModal}>
          <EditModalContainer $isMobile={isMobile} onClick={(e) => e.stopPropagation()}>
            <EditModalHeader>
              <EditModalTitle>
                {editingTask.isNew ? 'Новое задание онбординга' : 'Редактировать задание онбординга'}
              </EditModalTitle>
              <CloseBtn onClick={() => setEditingTask(null)}>
                <FiX />
              </CloseBtn>
            </EditModalHeader>
            <EditModalBody>
              <FormGroup>
                <Label $required>Имя</Label>
                <Input
                  type="text"
                  value={taskForm.name}
                  onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                  placeholder="Название задания"
                />
              </FormGroup>

              <FormGroup>
                <Label $required>Назначено</Label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Select
                    value={taskForm.assignedTo}
                    onChange={(e) => setTaskForm({ ...taskForm, assignedTo: e.target.value })}
                  >
                    <option value="person_concerned">Человек которого это касается</option>
                    <option value="specific">Конкретный человек</option>
                  </Select>
                  {taskForm.assignedTo === 'specific' && (
                    <Input
                      type="text"
                      value={taskForm.assignedPerson}
                      onChange={(e) => setTaskForm({ ...taskForm, assignedPerson: e.target.value })}
                      placeholder="Имя сотрудника"
                    />
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Дата начала</Label>
                <DateInputGroup>
                  <Select
                    value={taskForm.startDateType}
                    onChange={(e) => {
                      setTaskForm({ ...taskForm, startDateType: e.target.value, startDateValue: '', startSpecificDate: null });
                      setShowStartCalendar(false);
                    }}
                    style={{ flex: '0 0 250px' }}
                  >
                    <option value="days_before_hire">Дней до найма</option>
                    <option value="on_hire_date">На дату найма</option>
                    <option value="after_parent">После завершения родительского задания</option>
                    <option value="specific_date">Конкретная дата</option>
                  </Select>
                  {taskForm.startDateType === 'days_before_hire' && (
                    <>
                      <DateInput
                        type="number"
                        value={taskForm.startDateValue}
                        onChange={(e) => setTaskForm({ ...taskForm, startDateValue: e.target.value })}
                        placeholder="0"
                        style={{ flex: '0 0 100px' }}
                      />
                      <DateText>дней до найма</DateText>
                    </>
                  )}
                  {taskForm.startDateType === 'after_parent' && (
                    <DateText>Начинается после завершения родительского задания</DateText>
                  )}
                  {taskForm.startDateType === 'on_hire_date' && (
                    <DateText>На дату найма</DateText>
                  )}
                  {taskForm.startDateType === 'specific_date' && (
                    <CalendarWrapper ref={startCalendarRef}>
                      <CalendarButton
                        type="button"
                        onClick={() => setShowStartCalendar(!showStartCalendar)}
                      >
                        <FiCalendar />
                        {taskForm.startSpecificDate 
                          ? taskForm.startSpecificDate.toLocaleDateString('ru-RU')
                          : 'Выбрать дату'}
                      </CalendarButton>
                      {showStartCalendar && (
                        <CalendarDropdown>
                          <Calendar
                            onChange={(date) => {
                              setTaskForm({ ...taskForm, startSpecificDate: date });
                              setShowStartCalendar(false);
                            }}
                            value={taskForm.startSpecificDate}
                          />
                        </CalendarDropdown>
                      )}
                    </CalendarWrapper>
                  )}
                </DateInputGroup>
              </FormGroup>

              <FormGroup>
                <Label>Дата завершения</Label>
                <DateInputGroup>
                  <Select
                    value={taskForm.endDateType}
                    onChange={(e) => {
                      setTaskForm({ ...taskForm, endDateType: e.target.value, endDateValue: '', endSpecificDate: null });
                      setShowEndCalendar(false);
                    }}
                    style={{ flex: '0 0 250px' }}
                  >
                    <option value="days_after_start">Дней после даты начала</option>
                    <option value="same_day">Того же дня</option>
                    <option value="specific_date">Конкретная дата</option>
                  </Select>
                  {taskForm.endDateType === 'days_after_start' && (
                    <>
                      <DateInput
                        type="number"
                        value={taskForm.endDateValue}
                        onChange={(e) => setTaskForm({ ...taskForm, endDateValue: e.target.value })}
                        placeholder="0"
                        style={{ flex: '0 0 100px' }}
                      />
                      <DateText>дней после даты начала</DateText>
                    </>
                  )}
                  {taskForm.endDateType === 'same_day' && (
                    <DateText>Того же дня</DateText>
                  )}
                  {taskForm.endDateType === 'specific_date' && (
                    <CalendarWrapper ref={endCalendarRef}>
                      <CalendarButton
                        type="button"
                        onClick={() => setShowEndCalendar(!showEndCalendar)}
                      >
                        <FiCalendar />
                        {taskForm.endSpecificDate 
                          ? taskForm.endSpecificDate.toLocaleDateString('ru-RU')
                          : 'Выбрать дату'}
                      </CalendarButton>
                      {showEndCalendar && (
                        <CalendarDropdown>
                          <Calendar
                            onChange={(date) => {
                              setTaskForm({ ...taskForm, endSpecificDate: date });
                              setShowEndCalendar(false);
                            }}
                            value={taskForm.endSpecificDate}
                          />
                        </CalendarDropdown>
                      )}
                    </CalendarWrapper>
                  )}
                </DateInputGroup>
              </FormGroup>

              <FormGroup>
                <Label>Описание</Label>
                <RichTextEditor>
                  <EditorToolbar>
                    <ToolbarButton 
                      title="Жирный"
                      $bold
                      onClick={(e) => {
                        e.preventDefault();
                        document.execCommand('bold', false, null);
                        editorRef.current?.focus();
                      }}
                    >
                      <strong>B</strong>
                    </ToolbarButton>
                    <ToolbarButton 
                      title="Курсив"
                      onClick={(e) => {
                        e.preventDefault();
                        document.execCommand('italic', false, null);
                        editorRef.current?.focus();
                      }}
                    >
                      <em>I</em>
                    </ToolbarButton>
                    <ToolbarButton 
                      title="Подчеркнутый"
                      onClick={(e) => {
                        e.preventDefault();
                        document.execCommand('underline', false, null);
                        editorRef.current?.focus();
                      }}
                    >
                      <u>U</u>
                    </ToolbarButton>
                    <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />
                    <ToolbarButton 
                      title="Маркированный список"
                      onClick={(e) => {
                        e.preventDefault();
                        editorRef.current?.focus();
                        document.execCommand('insertUnorderedList', false, null);
                      }}
                    >
                      • Список
                    </ToolbarButton>
                    <ToolbarButton 
                      title="Нумерованный список"
                      onClick={(e) => {
                        e.preventDefault();
                        editorRef.current?.focus();
                        document.execCommand('insertOrderedList', false, null);
                      }}
                    >
                      1. Список
                    </ToolbarButton>
                    <div style={{ width: '1px', height: '24px', background: '#e5e7eb', margin: '0 4px' }} />
                    <ToolbarButton 
                      title="Ссылка"
                      onClick={(e) => {
                        e.preventDefault();
                        editorRef.current?.focus();
                        const url = prompt('Введите URL ссылки:', 'https://');
                        if (url) {
                          document.execCommand('createLink', false, url);
                        }
                      }}
                    >
                      🔗
                    </ToolbarButton>
                    <ToolbarButton 
                      title="Изображение"
                      onClick={(e) => {
                        e.preventDefault();
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (event) => {
                          const file = event.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              editorRef.current?.focus();
                              const img = document.createElement('img');
                              img.src = e.target.result;
                              img.style.maxWidth = '100%';
                              document.execCommand('insertHTML', false, img.outerHTML);
                            };
                            reader.readAsDataURL(file);
                          }
                        };
                        input.click();
                      }}
                    >
                      🖼️
                    </ToolbarButton>
                    <ToolbarButton 
                      title="Цвет текста"
                      onClick={(e) => {
                        e.preventDefault();
                        editorRef.current?.focus();
                        const color = prompt('Введите цвет (например: #ff0000 или red):', '#000000');
                        if (color) {
                          document.execCommand('foreColor', false, color);
                        }
                      }}
                    >
                      A
                    </ToolbarButton>
                  </EditorToolbar>
                  <EditorContent
                    ref={(node) => {
                      editorRef.current = node;
                      // Устанавливаем содержимое только при первом монтировании или изменении taskForm.description извне
                      if (node && !editorInitializedRef.current) {
                        node.innerHTML = taskForm.description || '<p><br></p>';
                        editorInitializedRef.current = true;
                      }
                    }}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => {
                      // Просто сохраняем HTML без обновления через React
                      const html = e.target.innerHTML;
                      setTaskForm({ ...taskForm, description: html || '' });
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const text = e.clipboardData.getData('text/plain');
                      const html = e.clipboardData.getData('text/html');
                      if (html) {
                        // Вставляем HTML с очисткой стилей
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = html;
                        // Убираем лишние стили, но сохраняем форматирование
                        const cleanHtml = tempDiv.innerHTML
                          .replace(/style="[^"]*"/gi, '')
                          .replace(/class="[^"]*"/gi, '');
                        document.execCommand('insertHTML', false, cleanHtml);
                      } else {
                        document.execCommand('insertText', false, text);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Обработка Enter для создания параграфов
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        document.execCommand('insertParagraph', false, null);
                      }
                    }}
                  />
                </RichTextEditor>
              </FormGroup>

              <SaveButton onClick={handleSaveTask}>
                <FiSave />
                Сохранить
              </SaveButton>
            </EditModalBody>
          </EditModalContainer>
        </EditModalOverlay>,
        document.body
      )}

      {showSendTaskModal && selectedTaskForSend &&
        createPortal(
        <EditModalOverlay $isMobile={isMobile} onClick={tryCloseSendTaskModal}>
          <EditModalContainer $isMobile={isMobile} onClick={(e) => e.stopPropagation()}>
            <EditModalHeader>
              <EditModalTitle>
                Отправить задачу: {selectedTaskForSend.name}
              </EditModalTitle>
              <CloseBtn onClick={() => setShowSendTaskModal(false)}>
                <FiX />
              </CloseBtn>
            </EditModalHeader>
            <EditModalBody>
              <FormGroup>
                <Label $required>Выберите пользователя</Label>
                <Select
                  value={selectedUserId}
                  onChange={(e) => {
                    const userId = e.target.value;
                    setSelectedUserId(userId);
                    const user = users.find(u => u.id === parseInt(userId));
                    setSelectedUserName(user ? (user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim()) : '');
                  }}
                >
                  <option value="">-- Выберите пользователя --</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User ${user.id}`}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Когда отправить?</Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <RadioChoiceLabel>
                    <input
                      type="radio"
                      checked={sendImmediately}
                      onChange={() => setSendImmediately(true)}
                    />
                    <span>Отправить немедленно</span>
                  </RadioChoiceLabel>
                  <RadioChoiceLabel>
                    <input
                      type="radio"
                      checked={!sendImmediately}
                      onChange={() => setSendImmediately(false)}
                    />
                    <span>Запланировать отправку</span>
                  </RadioChoiceLabel>
                </div>
              </FormGroup>

              {!sendImmediately && (
                <>
                  <FormGroup>
                    <Label $required>Дата отправки</Label>
                    <Input
                      type="date"
                      value={sendDate}
                      onChange={(e) => setSendDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label $required>Время отправки</Label>
                    <Input
                      type="time"
                      value={sendTime}
                      onChange={(e) => setSendTime(e.target.value)}
                    />
                  </FormGroup>
                </>
              )}

              <SaveButton onClick={handleConfirmSendTask}>
                <FiSend />
                {sendImmediately ? 'Отправить сейчас' : 'Запланировать отправку'}
              </SaveButton>
            </EditModalBody>
          </EditModalContainer>
        </EditModalOverlay>,
        document.body
      )}
    </>
  );
}
